/* TAB 3 — Strategy Analyser. Pick a ticker → live price card + candlestick chart
   + applicable strategies + position sizing.
   Exports calcStrategies for reuse by the Vault auto-scan in DayTradingPage. */
import { useState, useEffect } from "react";
import { C, Badge, RiskBar } from "./_shared";
import { usePrices, fetchPrice, fetchBars, TickerSearch } from "./_prices";
import CandlestickChart from "./CandlestickChart";
import { useCompanyNames } from "./_companyNames";

export function calcStrategies(d) {
  if (!d?.ok) return [];
  const rangePct  = d.prev ? (d.high-d.low)/d.prev*100 : 0;
  const volM      = d.vol/1e6;
  const aboveOpen = d.price > d.open;
  const gapPct    = d.prev ? (d.open-d.prev)/d.prev*100 : 0;
  const isGapUp   = gapPct > 2;
  const highVol   = volM > 5;
  const vHighVol  = volM > 20;
  const tight     = rangePct < 0.8;
  const wide      = rangePct > 2.5;
  const chgAbs    = Math.abs(d.chgPct);
  const strongMov = chgAbs > 2;
  const midMov    = chgAbs >= 0.5;

  function risk(base, extra) {
    const s = base + extra.reduce((a,b)=>a+b,0);
    return s<=2?"Low":s<=4?"Medium":s<=6?"High":"Very High";
  }

  const strats = [];

  if (vHighVol) strats.push({
    id:1, name:"Order Flow Scalp", style:"Scalping", color:C.red, colorBg:C.rb, colorText:C.rt,
    ok:true, risk:risk(2,[wide?2:0,strongMov?1:0]),
    hold:"30s–3min", rrMin:"1.5:1", maxRisk:"0.25%",
    why:`Vol ${volM.toFixed(1)}M — ideal for tape reading. ${wide?"Wide range: reduce size.":"Tight range: cleaner scalp."}`,
    entry:`$${d.price.toFixed(2)}`, stop:`$${(d.price*0.9975).toFixed(2)}`, target:`$${(d.price*1.004).toFixed(2)}`,
    conds:[vHighVol?"✓ Volume >20M confirmed":"✗ Volume too low",aboveOpen?"✓ Above open (bull bias)":"✗ Below open",!wide?"✓ Range manageable":"⚠ Wide range — half size"],
  });

  if (highVol) strats.push({
    id:2, name:"Opening Range Breakout", style:"Scalping", color:C.red, colorBg:C.rb, colorText:C.rt,
    ok:true, risk:risk(1,[isGapUp?-1:1,!tight?1:0]),
    hold:"2–5min", rrMin:"2:1", maxRisk:"1%",
    why:`${isGapUp?`Gapped up ${gapPct.toFixed(1)}% — ORB ideal.`:"No gap — wait for range to form."} Volume: ${highVol?"confirmed.":"needs work."}`,
    entry:`$${d.price.toFixed(2)}`, stop:`$${(d.low*0.999).toFixed(2)}`, target:`$${(d.price+(d.high-d.low)).toFixed(2)}`,
    conds:[isGapUp?"✓ Gap up — catalyst present":"⚠ No gap — lower probability",highVol?"✓ Volume supports break":"✗ Volume missing",!wide?"✓ Range manageable":"⚠ Reduce size"],
  });

  if (midMov||strongMov) strats.push({
    id:3, name:"Momentum + VWAP", style:"Momentum", color:C.amber, colorBg:C.ab, colorText:C.at,
    ok:highVol, risk:risk(2,[strongMov?1:0,!highVol?2:0]),
    hold:"5–20min", rrMin:"2:1", maxRisk:"1%",
    why:`${chgAbs.toFixed(1)}% move, ${volM.toFixed(1)}M vol. ${aboveOpen?"Above open = bull momentum.":"Below open — wait for VWAP reclaim."}`,
    entry:`$${d.price.toFixed(2)}`, stop:`$${(d.price*0.995).toFixed(2)}`, target:`$${(d.price*1.01).toFixed(2)}`,
    conds:[aboveOpen?"✓ Above open":"✗ Below open — skip",highVol?"✓ Volume confirms":"✗ Low volume",chgAbs<4?"✓ Not overextended":"⚠ Chasing risk"],
  });

  if (isGapUp) strats.push({
    id:4, name:"Gap & Go", style:"Momentum", color:C.amber, colorBg:C.ab, colorText:C.at,
    ok:isGapUp&&highVol, risk:risk(2,[chgAbs>5?2:0,!highVol?2:0]),
    hold:"10–30min", rrMin:"2.5:1", maxRisk:"1%",
    why:`Gap up ${gapPct.toFixed(1)}%. ${vHighVol?"Strong catalyst volume.":highVol?"Volume ok.":"Volume weak — higher risk."}`,
    entry:`$${d.price.toFixed(2)}`, stop:`$${(d.open*0.997).toFixed(2)}`, target:`$${(d.price*1.015).toFixed(2)}`,
    conds:[`${isGapUp?"✓":"✗"} Gap ${gapPct.toFixed(1)}%`,highVol?"✓ Volume confirms":"✗ Low volume",chgAbs<6?"✓ Not over-extended":"⚠ Extended — take partial"],
  });

  if (tight) strats.push({
    id:5, name:"BB Squeeze Breakout", style:"Momentum", color:C.amber, colorBg:C.ab, colorText:C.at,
    ok:tight, risk:risk(2,[!highVol?1:0]),
    hold:"5–15min", rrMin:"2:1", maxRisk:"1%",
    why:`Range only ${rangePct.toFixed(1)}% — BB likely compressing. ${highVol?"Volume building.":"Watch for vol surge."}`,
    entry:`$${d.price.toFixed(2)}`, stop:`$${(d.low*0.998).toFixed(2)}`, target:`$${(d.high+(d.high-d.low)*1.5).toFixed(2)}`,
    conds:[tight?"✓ Tight range — squeeze active":"✗ Too wide","⚠ Wait for breakout candle + volume",highVol?"✓ Volume building":"⚠ Need vol to confirm"],
  });

  strats.push({
    id:6, name:"VWAP Anchored Trade", style:"Swing", color:C.indigo, colorBg:C.ib, colorText:C.it,
    ok:highVol, risk:risk(2,[wide?1:0,!highVol?1:0]),
    hold:"30min–2hr", rrMin:"2.5:1", maxRisk:"1%",
    why:`${aboveOpen?"Above open — VWAP likely support. Pullback to VWAP = entry.":"Below open — wait for VWAP reclaim before long."}`,
    entry:`$${d.price.toFixed(2)}`, stop:`$${d.low.toFixed(2)}`, target:`$${(d.high+(d.high-d.low)*0.5).toFixed(2)}`,
    conds:[aboveOpen?"✓ Bull session bias":"⚠ Wait for reclaim",highVol?"✓ Volume valid":"✗ Low vol — skip",!wide?"✓ Clean range":"⚠ Wide range — bigger stop"],
  });

  if (strongMov&&highVol) strats.push({
    id:7, name:"Open→Power Hour Ride", style:"Swing", color:C.indigo, colorBg:C.ib, colorText:C.it,
    ok:strongMov&&highVol, risk:risk(3,[wide?2:0]),
    hold:"1–3hr", rrMin:"3:1", maxRisk:"1.5%",
    why:`Strong ${chgAbs.toFixed(1)}% move with ${volM.toFixed(1)}M vol — institutional backing. Enter open, target Power Hour 3–4pm.`,
    entry:`$${d.price.toFixed(2)}`, stop:`$${(d.low*0.997).toFixed(2)}`, target:`$${(d.price*1.02).toFixed(2)}`,
    conds:[strongMov?"✓ Strong trend move":"✗ Weak move",highVol?"✓ Institutional vol":"✗ Low vol",aboveOpen?"✓ Bull session":"⚠ Mixed session"],
  });

  return strats.sort((a,b)=>(a.ok===b.ok?0:a.ok?-1:1));
}

export default function StrategyAnalyserTab({ analyseRequest, clearAnalyseRequest, onBack }) {
  const [result,    setResult]   = useState(null);
  const [searching, setSearching]= useState(false);
  // Account size — shared with Options planner + Position Sizer via
  // the unified `dt_account_size` localStorage key. Blank by default so
  // users enter their real size instead of trusting a fake $10k default.
  const [accountRaw, setAccountRaw]  = useState(() => {
    try {
      // Migrate old `dt_analyser_account` value once if present.
      const legacy = localStorage.getItem("dt_analyser_account");
      const current = localStorage.getItem("dt_account_size");
      if (!current && legacy) { localStorage.setItem("dt_account_size", legacy); localStorage.removeItem("dt_analyser_account"); return legacy; }
      return current || "";
    } catch { return ""; }
  });
  useEffect(() => {
    try {
      if (accountRaw === "") localStorage.removeItem("dt_account_size");
      else localStorage.setItem("dt_account_size", String(accountRaw));
    } catch { /* noop */ }
  }, [accountRaw]);
  const account = (() => {
    const n = parseFloat(accountRaw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  })();
  const [expanded,  setExpanded] = useState(null);
  const [bars,      setBars]     = useState(null);
  const [barsLoading, setBarsLoading] = useState(false);
  const { prices } = usePrices();
  const { name: companyName } = useCompanyNames();

  async function loadTicker(item) {
    if (!item || !item.symbol) return;
    const t = item.symbol.toUpperCase();
    // Always prefer the bulk-fetched company name over the partial name from
    // the search autocomplete — autocomplete may give "AAPL" if the user
    // typed the ticker directly.
    const fullName = companyName(t, item.name || t);
    setExpanded(null);
    setBars(null);
    if (prices[t]?.ok) { setResult({ ...prices[t], _name: fullName }); }
    else {
      setSearching(true);
      const d = await fetchPrice(t);
      setResult({ ...d, _name: fullName });
      setSearching(false);
    }
    // Fire bars fetch in parallel — non-blocking
    setBarsLoading(true);
    fetchBars(t, { multiplier: 5, limit: 78 })
      .then(b => setBars(b))
      .finally(() => setBarsLoading(false));
  }

  // ── Auto-load when arriving from Scanner / Market Watch via requestAnalyse ──
  // Reuses live price data from PriceProvider when available — no extra API call.
  useEffect(() => {
    if (!analyseRequest?.ticker) return;
    const t = analyseRequest.ticker.toUpperCase();
    loadTicker({ symbol: t, name: t });
    // We intentionally do NOT clear the request here so the "← Back" button
    // still works. It is cleared on unmount or when the user picks another
    // ticker via search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyseRequest?.ticker]);

  // Re-fetch bars if user clicks the same ticker again or wants fresh data
  useEffect(() => {
    if (!result?.ok) return;
    setBarsLoading(true);
    fetchBars(result.ticker, { multiplier: 5, limit: 78 })
      .then(b => setBars(b))
      .finally(() => setBarsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.ticker]);

  const strats   = result?.ok ? calcStrategies(result) : [];
  const active   = strats.filter(s=>s.ok);
  const watching = strats.filter(s=>!s.ok);
  const isUp     = result?.chg>=0;

  const RISK_MAP = { Low:{c:C.green,pct:20,bg:C.gb,text:C.gt}, Medium:{c:C.amber,pct:50,bg:C.ab,text:C.at}, High:{c:C.red,pct:75,bg:C.rb,text:C.rt}, "Very High":{c:C.purple,pct:100,bg:C.pb,text:C.pt} };

  function sharesCalc(s) {
    if (!result||!s.stop) return "—";
    const riskDol = account * parseFloat(s.maxRisk) / 100;
    const stopDist = Math.abs(result.price - parseFloat(s.stop.replace("$","")));
    return stopDist > 0 ? `${Math.floor(riskDol/stopDist)} shares` : "—";
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {analyseRequest?.ticker && typeof onBack === "function" && (
        <div data-testid="analyser-back-banner" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:C.ib, border:`1px solid ${C.indigo}33`, borderRadius:9, padding:"8px 14px" }}>
          <button
            onClick={() => { if (typeof clearAnalyseRequest === "function") clearAnalyseRequest(); onBack(); }}
            data-testid="analyser-back-btn"
            style={{ background:"transparent", border:"none", color:C.indigo, fontSize:12, fontWeight:700, cursor:"pointer", padding:0 }}
          >
            ← Back
          </button>
          <span style={{ fontSize:11, color:C.indigo, fontWeight:600 }}>
            Analysing <b>{analyseRequest.ticker}</b> from {analyseRequest.sourceTab === 1 ? "Market Watch" : analyseRequest.sourceTab === 3 ? "Scanner" : "previous tab"}
          </span>
        </div>
      )}

      <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:8, padding:"14px 18px", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:10, fontWeight:500, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>Strategy Analyser</div>
        <p style={{ fontSize:12, color:"#64748b", marginBottom:12, lineHeight:1.5 }}>Type a ticker or company name (e.g. "Tesla" or "TSLA") — pick from suggestions to fetch live price &amp; strategies.</p>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:12 }}>
          <label style={{ fontSize:10, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:500 }}>Account size</label>
          <div style={{ display:"flex", borderRadius:6, border:"1px solid #d1d5db", overflow:"hidden" }}>
            <span style={{ padding:"7px 10px", background:"#f9fafb", fontSize:12, color:"#64748b", borderRight:"1px solid #d1d5db", fontFamily:"monospace" }}>$</span>
            <input type="number" value={accountRaw} onChange={e=>setAccountRaw(e.target.value)} placeholder="Enter your account size" data-testid="analyser-account-input" style={{ padding:"7px 10px", border:"none", fontSize:12, width:170, fontFamily:"monospace" }}/>
          </div>
          {searching && <span style={{ fontSize:11, color:"#4f46e5", fontWeight:600 }}>Fetching live price···</span>}
        </div>
        <TickerSearch onSelect={loadTicker} placeholder='Search any US stock — type "Tesla" or "TSLA"…' />
      </div>

      {result?.ok && (
        <>
          <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderLeft:`3px solid ${isUp?"#16a34a":"#dc2626"}`, borderRadius:8, padding:"14px 18px", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", marginBottom:8 }}>
              <span style={{ fontSize:22, fontWeight:700, color:"#0f172a", fontFamily:"monospace" }}>{result.ticker}</span>
              {result._name && result._name !== result.ticker && (
                <span style={{ fontSize:13, color:"#64748b", fontWeight:500 }}>· {result._name}</span>
              )}
              <span style={{ fontSize:24, fontWeight:700, color:isUp?"#16a34a":"#dc2626", fontFamily:"monospace" }}>${result.price.toFixed(2)}</span>
              <span style={{ fontSize:14, fontWeight:600, color:isUp?"#16a34a":"#dc2626", fontFamily:"monospace" }}>{isUp?"+":""}{result.chg.toFixed(2)} ({isUp?"+":""}{result.chgPct.toFixed(2)}%)</span>
              <Badge label={result.state} bg="rgba(0,0,0,0.07)" text={isUp?"#16a34a":"#dc2626"}/>
              <span style={{ fontSize:11, color:"#94a3b8", marginLeft:"auto" }}>{active.length} strategies active · {watching.length} watching</span>
            </div>
            <div style={{ display:"flex", gap:14, flexWrap:"wrap", fontSize:11, color:"#64748b", fontFamily:"monospace" }}>
              <span>O: <b style={{color:"#0f172a"}}>${result.open.toFixed(2)}</b></span>
              <span>H: <b style={{color:"#0f172a"}}>${result.high.toFixed(2)}</b></span>
              <span>L: <b style={{color:"#0f172a"}}>${result.low.toFixed(2)}</b></span>
              <span>Prev: <b style={{color:"#0f172a"}}>${result.prev.toFixed(2)}</b></span>
              <span>Vol: <b style={{color:"#0f172a"}}>{result.vol>=1e6?`${(result.vol/1e6).toFixed(1)}M`:`${(result.vol/1e3).toFixed(0)}K`}</b></span>
              <span>Range: <b style={{color:"#0f172a"}}>{((result.high-result.low)/result.prev*100).toFixed(2)}%</b></span>
            </div>
          </div>

          {/* 5-min candlestick chart with EMA9/EMA21/VWAP overlays + volume + RSI */}
          {/* ── Fundamental score (multi-factor heuristic) ───────────────
              Composite of momentum, trend and volatility scores. All three
              factors derived from the live price/bars we already have on
              hand — no extra API call needed. */}
          <FundamentalScore result={result} bars={bars} />

          {barsLoading && !bars && (
            <div style={{ background:"#fff", border:"0.5px solid #e5e7eb", borderRadius:10, padding:"40px 16px", textAlign:"center", fontSize:12, color:C.gray }}>
              Loading 5-min chart…
            </div>
          )}
          {bars && bars.length >= 2 && (
            <CandlestickChart bars={bars} ticker={`${result.ticker} · 5m`}/>
          )}
          {bars && bars.length < 2 && !barsLoading && (
            <div style={{ background:"#fff", border:"0.5px solid #e5e7eb", borderRadius:10, padding:"24px 16px", textAlign:"center", fontSize:12, color:C.muted }}>
              No intraday bars available — market may be closed.
            </div>
          )}

          {active.length>0 && (
            <div>
              <p style={{ fontSize:11, fontWeight:700, color:C.green, letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:8 }}>✓ {active.length} applicable strateg{active.length===1?"y":"ies"}</p>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {active.map(s => {
                  const rc = RISK_MAP[s.risk]||RISK_MAP.Medium;
                  const open = expanded===s.id;
                  return (
                    <div key={s.id} style={{ background:"#fff", border:`1px solid ${open?s.color+"66":"#e5e7eb"}`, borderRadius:9, overflow:"hidden", borderLeft:`4px solid ${s.color}` }}>
                      <div onClick={()=>setExpanded(open?null:s.id)} style={{ padding:"11px 13px", cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:4 }}>
                            <span style={{ fontSize:13, fontWeight:700, color:"#111" }}>{s.name}</span>
                            <Badge label={s.style} bg={s.colorBg} text={s.colorText}/>
                            <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4, background:rc.bg, color:rc.text }}>{s.risk} Risk</span>
                            <span style={{ fontSize:10, color:C.gray }}>Hold: {s.hold} · Min R:R {s.rrMin}</span>
                          </div>
                          <RiskBar level={s.risk}/>
                        </div>
                        <span style={{ fontSize:11, color:C.gray }}>{open?"▲":"▼"}</span>
                      </div>
                      {open && (
                        <div style={{ padding:"0 13px 13px", borderTop:"0.5px solid #f3f4f6", background:"#fafafa" }}>
                          <p style={{ fontSize:12, color:C.gray, lineHeight:1.65, margin:"10px 0" }}>{s.why}</p>
                          <div style={{ display:"flex", flexDirection:"column", gap:3, marginBottom:10 }}>
                            {s.conds.map((c,i)=>(
                              <div key={i} style={{ fontSize:12, color:c.startsWith("✓")?C.gt:c.startsWith("✗")?C.rt:C.at, display:"flex", gap:6 }}>
                                <span>{c.slice(0,1)}</span><span>{c.slice(2)}</span>
                              </div>
                            ))}
                          </div>
                          <div style={{ background:"#fff", border:"0.5px solid #e5e7eb", borderRadius:8, padding:"10px 12px" }}>
                            <p style={{ fontSize:11, fontWeight:700, color:"#111", marginBottom:8 }}>Position sizing — {account > 0 ? `$${account.toLocaleString()} account` : <span style={{ color:C.amber }}>⚠ Enter account size to see sizing</span>}</p>
                            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(90px,1fr))", gap:7 }}>
                              {[
                                { l:"Entry",    v:s.entry,              c:"#111" },
                                { l:"Stop",     v:s.stop,               c:C.red },
                                { l:"Target",   v:s.target,             c:C.green },
                                { l:"R:R",      v:s.rrMin,              c:C.blue },
                                { l:"Max risk", v:`${s.maxRisk} = $${(account*parseFloat(s.maxRisk)/100).toFixed(0)}`, c:C.amber },
                                { l:"Shares",   v:sharesCalc(s),        c:C.indigo },
                              ].map(item=>(
                                <div key={item.l} style={{ background:C.gbg, borderRadius:6, padding:"7px 9px" }}>
                                  <div style={{ fontSize:10, color:C.gray, marginBottom:2 }}>{item.l}</div>
                                  <div style={{ fontSize:12, fontWeight:700, color:item.c }}>{item.v}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {watching.length>0 && (
            <div>
              <p style={{ fontSize:11, fontWeight:700, color:C.amber, letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:8 }}>⚠ {watching.length} strategies — conditions not yet met</p>
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                {watching.map(s=>(
                  <div key={s.id} style={{ background:"#f9fafb", border:"0.5px solid #e5e7eb", borderRadius:8, padding:"9px 12px", display:"flex", alignItems:"center", gap:10, opacity:0.7 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:2 }}>
                        <span style={{ fontSize:12, fontWeight:600, color:C.gray }}>{s.name}</span>
                        <Badge label={s.style} bg={s.colorBg} text={s.colorText} size={10}/>
                        <span style={{ fontSize:10, fontWeight:600, color:RISK_MAP[s.risk]?.c||C.amber }}>{s.risk} Risk</span>
                      </div>
                      <p style={{ fontSize:11, color:C.muted }}>{s.why}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {result&&!result.ok && <div style={{ background:C.rb, borderRadius:9, padding:"14px 16px", fontSize:13, color:C.rt }}>Could not fetch <b>{result.ticker}</b> — check the symbol and try again.</div>}
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────
   FundamentalScore — multi-factor 0-100 composite, computed from
   the live price + intraday bars already in scope. No extra API
   call. Heuristic, not a buy/sell recommendation.
   ──────────────────────────────────────────────────────────────── */
function rsiFromBars(bars, period = 14) {
  if (!bars || bars.length < period + 1) return null;
  let gains = 0, losses = 0;
  for (let i = bars.length - period; i < bars.length; i++) {
    const d = bars[i].c - bars[i - 1].c;
    if (d >= 0) gains += d; else losses -= d;
  }
  if (gains + losses === 0) return 50;
  const rs = gains / Math.max(0.0001, losses);
  return 100 - 100 / (1 + rs);
}

function FundamentalScore({ result, bars }) {
  if (!result?.ok) return null;
  const rsi = rsiFromBars(bars);
  // Momentum: RSI band + price-vs-recent-base
  let momentum = 50;
  if (rsi != null) {
    if (rsi >= 40 && rsi <= 70) momentum = 60;
    else if (rsi > 70)            momentum = 40;
    else                          momentum = 30;
  }
  if (bars && bars.length >= 12) {
    const last  = bars[bars.length - 1].c;
    const sma12 = bars.slice(-12).reduce((s, b) => s + b.c, 0) / 12;
    const sma50 = bars.slice(-50).reduce((s, b) => s + b.c, 0) / Math.min(50, bars.length);
    if (last > sma12) momentum += 20;
    if (last > sma50) momentum += 20;
  }
  momentum = Math.max(0, Math.min(100, momentum));

  // Trend: bars-derived SMA + day-direction + volume
  let trend = 0;
  if (bars && bars.length >= 12) {
    const last  = bars[bars.length - 1].c;
    const sma12 = bars.slice(-12).reduce((s, b) => s + b.c, 0) / 12;
    const smaLong = bars.slice(0, Math.floor(bars.length / 2)).reduce((s, b) => s + b.c, 0) / Math.max(1, Math.floor(bars.length / 2));
    if (last > smaLong) trend += 60;
    if (last > sma12)   trend += 20;
  } else {
    trend += result.chg >= 0 ? 60 : 0;
  }
  if (result.chg >= 0)            trend += 10;
  const intraRange = (result.high - result.low) / Math.max(0.0001, result.prev);
  if (intraRange > 0.005)         trend += 10;  // any meaningful intraday move
  trend = Math.max(0, Math.min(100, trend));

  // Volatility: lower intraday range = higher score (we don't have beta)
  let vol;
  const dailyPct = Math.abs(result.chgPct);
  if (dailyPct < 1)        vol = 80;
  else if (dailyPct < 2)   vol = 60;
  else if (dailyPct < 4)   vol = 40;
  else                     vol = 20;

  const composite = Math.round((momentum + trend + vol) / 3);
  const compColor = composite >= 70 ? C.green : composite >= 40 ? C.amber : C.red;
  const verdict   = composite >= 70 ? "Strong setup — conditions favour a trade"
                  : composite >= 40 ? "Selective — wait for confirmation"
                  :                   "Avoid — conditions unfavourable";

  const cardStyle = { background: "#f9fafb", borderRadius: 7, padding: "10px 12px" };
  const labelStyle = { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: C.muted, fontWeight: 500 };
  const valStyle   = { fontSize: 20, fontWeight: 700, fontFamily: "monospace", marginTop: 4 };

  return (
    <div data-testid="fundamental-score" style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#111", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 10 }}>Fundamental Score</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
        <div style={cardStyle}><div style={labelStyle}>Momentum</div><div style={{ ...valStyle, color: C.navy || "#111" }}>{momentum}</div></div>
        <div style={cardStyle}><div style={labelStyle}>Trend</div><div style={{ ...valStyle, color: C.navy || "#111" }}>{trend}</div></div>
        <div style={cardStyle}><div style={labelStyle}>Volatility</div><div style={{ ...valStyle, color: C.navy || "#111" }}>{vol}</div></div>
        <div style={cardStyle}><div style={labelStyle}>Composite</div><div data-testid="score-composite" style={{ ...valStyle, color: compColor }}>{composite}</div></div>
      </div>
      <div data-testid="score-verdict" style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: compColor }}>{verdict}</div>
      <div style={{ marginTop: 4, fontSize: 9, color: C.muted, lineHeight: 1.6 }}>Score based on price action and volatility only. Not a buy/sell recommendation.</div>
    </div>
  );
}
